import { nevisignFetch } from "@nevisign/reader-core";
import type {
  ContractWorkflowStateDTO,
  SignatureStepDTO,
} from "@platform/shared-types";
import { useAuthStore } from "../../stores/auth-store";
import type { ReaderSigningSession } from "./types";

const PENDING_STATUSES = new Set(["pending", "seen", "commented"]);

function findOpenStage(signers: SignatureStepDTO[]): number | null {
  const sorted = [...signers].sort((a, b) => a.order - b.order);
  for (const s of sorted) {
    if (s.status !== "signed" && s.status !== "rejected") {
      return s.order;
    }
  }
  return null;
}

export function findPendingSignatureForUser(
  workflow: ContractWorkflowStateDTO,
  userId: string,
): ReaderSigningSession | null {
  const sig = workflow.activeSignature;
  if (!sig || sig.status !== "active") return null;

  const myStep = sig.signers.find((s) => s.userId === userId);
  if (!myStep || !PENDING_STATUSES.has(myStep.status)) return null;

  const openStage = findOpenStage(sig.signers);
  const isMyStage = openStage != null && myStep.order === openStage;

  return {
    contractId: sig.contractId,
    processId: sig.id,
    mode: sig.mode,
    gatewayToken: myStep.gatewayToken ?? null,
    signerStatus: myStep.status,
    canAct: isMyStage,
    isMyStage,
    processStatus: sig.status,
  };
}

export async function fetchContractWorkflow(
  apiBase: string,
  contractId: string,
): Promise<ContractWorkflowStateDTO | null> {
  try {
    const res = await nevisignFetch(
      apiBase,
      `/contracts/${encodeURIComponent(contractId)}/workflow`,
      { cache: "no-store" },
    );
    if (res.status === 401) return null;
    const text = await res.text();
    if (!text.trim()) return null;
    return JSON.parse(text) as ContractWorkflowStateDTO;
  } catch {
    return null;
  }
}

export async function detectPendingSignature(
  apiBase: string,
  contractId: string,
): Promise<ReaderSigningSession | null> {
  const userId = useAuthStore.getState().user?.id;
  if (!userId) return null;
  const workflow = await fetchContractWorkflow(apiBase, contractId);
  if (!workflow) return null;
  return findPendingSignatureForUser(workflow, userId);
}
